import React, { useState, useMemo, memo, useCallback, useEffect } from "react";
import {
  Accordion,
  Card,
  FormCheck,
  FormControl,
  Badge,
  Button,
  InputGroup,
  Spinner
} from "react-bootstrap";
import { useFormikContext } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { FaExternalLinkAlt, FaSearch, FaBoxes, FaBatteryFull, FaChevronDown, FaSync } from "react-icons/fa";
import { formatCurrency } from "../../utility";
import StockDetails from "../stock/StockDetails";
import { getAllItemsWithStock } from "../../redux/features/stock.features";

// Custom High-Fidelity Styles
const styles = `
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.4; }
  }
  .animate-pulse {
    animation: pulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  }
  .transition-all {
    transition: all 0.2s ease-in-out;
  }
  .search-input-group:focus-within {
    border-color: #3b82f6 !important;
    box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.15) !important;
  }
  .hover-bg-light:hover {
    background-color: #f8fafc !important;
  }
`;

// ----------------------------------------------------------------------
// Memoized Helper Components
// ----------------------------------------------------------------------

const StockDetailsLink = memo(({ purchaseId, setViewingPurchase }) => {
  return (
    <Button
      variant="link"
      size="sm"
      className="p-0 text-decoration-none d-flex align-items-center gap-1"
      onClick={() => setViewingPurchase(purchaseId)}
      disabled={!purchaseId}
    >
      Details <FaExternalLinkAlt size={10} />
    </Button>
  );
});

const BatchTable = ({ item, batchNumber, batchItems, toggleBatch, updateBatchQty, setViewingPurchase }) => {
  const { values } = useFormikContext();

  // Find the parent item in printItems using the item's unique _id
  const parentItem = values.printItems.find(p => p._id === item._id);
  const isSelected = parentItem?.batchNumbers?.includes(batchNumber) || false;
  const isSerialized = item.serialized || item.isSerialized;

  return (
    <table className="table table-sm mb-0">
      <thead className="bg-light">
        <tr className="text-secondary fw-bold" style={{ fontSize: '0.75rem' }}>
          <th style={{ width: '30px' }}></th>
          {isSerialized && <th>Serial</th>}
          <th>Stock</th>
          <th style={{ width: '100px' }}>Print Qty</th>
          <th>Ref #</th>
          <th>Cost</th>
          <th>Sell</th>
          <th>B2B</th>
        </tr>
      </thead>
      <tbody>
        {batchItems.map((batchItem, idx) => {

          // Check if this specific serial row is selected
          const hasSerialData = !!batchItem.serialNumber;
          let serialSelected = false;

          if (isSelected && isSerialized && parentItem && hasSerialData) {
            serialSelected = parentItem.serializedItems?.some(s => s.serialNumber === batchItem.serialNumber);
          } else if (isSelected) {
            serialSelected = true; // Batch is selected
          }

          // Determine if we show a quantity input box
          // Show input if: Not Serialized OR Serialized but missing specific serial data (Bulk mode)
          const showQtyInput = !isSerialized || !hasSerialData;
          const currentQty = parentItem?.batchData?.[batchNumber]?.qty ?? "";

          return (
            <tr key={idx} className="align-middle">
              <td>
                <FormCheck
                  type="checkbox"
                  checked={isSerialized && hasSerialData ? serialSelected : isSelected}
                  onChange={() => toggleBatch(item, batchNumber, (isSerialized && hasSerialData) ? batchItem.serialNumber : null)}
                  className="m-0"
                  disabled={showQtyInput && !isSelected && !currentQty} // Optional: Disable check if using qty input logic primarily
                />
              </td>
              {isSerialized && (
                <td className="small font-monospace">
                  <div className="d-flex align-items-center gap-2">
                    {batchItem.serialNumber ? (
                      <>
                        <span className="text-dark fw-bold">{batchItem.serialNumber}</span>
                        {batchItem.batteryHealth && (
                          <Badge bg={batchItem.batteryHealth >= 80 ? "success" : "warning"} pill style={{ fontSize: '0.6rem' }}>
                            <FaBatteryFull className="me-1" />{batchItem.batteryHealth}%
                          </Badge>
                        )}
                      </>
                    ) : (
                      <span className="text-muted fst-italic">Bulk / N/A</span>
                    )}
                  </div>
                </td>
              )}
              <td className="small text-muted">{batchItem.availableQty}</td>
              <td>
                {showQtyInput ? (
                  <div className="d-flex align-items-center">
                    <FormControl
                      size="sm"
                      type="number"
                      min="0"
                      max={batchItem.availableQty}
                      value={currentQty}
                      placeholder="0"
                      onChange={(e) => {
                        // Auto-select batch when typing
                        const val = e.target.value === "" ? "" : parseInt(e.target.value, 10);
                        updateBatchQty(item, batchNumber, val, batchItem);
                      }}
                      className={`text-center p-1 fw-bold border ${isSelected ? 'border-primary bg-white' : 'border-secondary-subtle bg-light'}`}
                      style={{ maxWidth: '60px' }}
                    />
                    <span className="text-muted x-small ms-1 opacity-75">/ {batchItem.availableQty}</span>
                  </div>
                ) : (
                  <span className="text-muted small ps-2">1 Label</span>
                )}
              </td>
              <td className="small text-muted">{batchItem.invoiceNumber || batchItem.purchase?.referenceNumber || '-'}</td>
              <td className="small text-muted">{formatCurrency(batchItem.unitCost)}</td>
              <td className="small fw-bold text-dark">{formatCurrency(batchItem.sellingPrice)}</td>
              <td className="small fw-bold text-primary">
                {batchItem.b2bPrice || (batchItem.b2bPricing && batchItem.b2bPricing[0]?.price) || (item.b2bPricing && item.b2bPricing[0]?.price)
                  ? formatCurrency(batchItem.b2bPrice || (batchItem.b2bPricing && batchItem.b2bPricing[0]?.price) || (item.b2bPricing && item.b2bPricing[0]?.price))
                  : '-'}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

const ItemRow = ({ item, isItemSelected, isBatchSelected, toggleItem, toggleBatch, updateBatchQty, setViewingPurchase }) => {
  // Normalize batches for display
  const groupedItems = useMemo(() => {
    // 1. Try to get detailed Serial Items first
    let source = (item.serialized) ? (item.serializedItems || []) : (item.batches || []);

    // 2. Fallback: If Serialized but list is empty, use Batches or Virtual
    if ((item.serialized || item.isSerialized) && source.length === 0) {
      if (item.batches?.length > 0) {
        source = item.batches;
      } else if (item.totalStock > 0) {
        source = [{
          batch_number: "AGGREGATED",
          availableQty: item.totalStock,
          unitCost: item.lastUnitCost || 0,
          sellingPrice: item.lastSellingPrice || 0,
          serialNumber: null
        }];
      }
    } else if (!item.serialized && !item.isSerialized && source.length === 0 && item.totalStock > 0) {
      source = [{
        batch_number: "AGGREGATED",
        availableQty: item.totalStock,
        unitCost: item.lastUnitCost || 0,
        sellingPrice: item.lastSellingPrice || 0
      }];
    }

    return source.reduce((acc, batchItem) => {
      const bNum = batchItem.batch_number || "NO_BATCH";
      if (!acc[bNum]) acc[bNum] = [];
      acc[bNum].push(batchItem);
      return acc;
    }, {});
  }, [item]);

  const stockCount = item.totalStock || item.quantity || 0;
  const isSerialized = item.serialized || item.isSerialized;

  const { values } = useFormikContext();
  const parentItem = (values.printItems || []).find(p => p._id === item._id);
  let selectedLabelCount = 0;
  if (parentItem) {
    if (isSerialized) {
      selectedLabelCount = parentItem.serializedItems?.length || 0;
    } else {
      if (parentItem.batchData && Object.keys(parentItem.batchData).length > 0) {
        Object.values(parentItem.batchData).forEach(b => {
          selectedLabelCount += Number(b.qty) || 0;
        });
      } else {
        selectedLabelCount = parentItem.batchNumbers?.length || 1;
      }
    }
  }

  return (
    <Card className={`mb-3 shadow-sm border transition-all ${isItemSelected ? 'border-primary' : 'border-0'}`} style={isItemSelected ? { backgroundColor: 'rgba(59, 130, 246, 0.02)' } : {}}>
      <Accordion.Item eventKey={item._id} className="border rounded overflow-hidden">
        <Accordion.Header>
          <div className="d-flex justify-content-between align-items-center w-100 pe-3">
            <div className="d-flex align-items-center gap-3">
              <div onClick={(e) => e.stopPropagation()}>
                <FormCheck
                  type="checkbox"
                  checked={isItemSelected}
                  onChange={() => toggleItem(item)}
                  className="scale-125"
                />
              </div>
              <div className="d-flex flex-column">
                <span className="fw-bold text-dark">
                  {item.variantName && !item.itemName?.toLowerCase().includes(item.variantName?.toLowerCase())
                    ? `${item.itemName} - ${item.variantName}`
                    : (item.variantName || item.itemName)}
                </span>
                {item.variantAttributes && item.variantAttributes.length > 0 && (
                  <div className="d-flex flex-wrap gap-1 mt-1">
                    {item.variantAttributes.map((attr, idx) => (
                      <Badge key={idx} bg="light" text="dark" className="border fw-normal x-small">
                        {attr.value}
                      </Badge>
                    ))}
                  </div>
                )}

                <div className="d-flex align-items-center gap-2 mt-1">
                  <Badge bg="light" text="dark" className="border font-monospace">
                    {item.barcode || 'NO BARCODE'}
                  </Badge>
                  <Badge bg={stockCount > 0 ? "success" : "danger"} className="shadow-none">
                    {stockCount} in Stock
                  </Badge>
                  <Badge bg="dark" text="white" className="shadow-none">
                    {isSerialized ? "Serialized" : "Bulk"}
                  </Badge>
                </div>
              </div>
            </div>

            <div className="d-flex align-items-center gap-2">
              {isItemSelected && selectedLabelCount > 0 && (
                <Badge bg="primary" pill className="px-3 py-1.5 fw-bold font-monospace shadow-xs d-flex align-items-center gap-1" style={{ fontSize: '0.78rem' }}>
                  <span className="bg-white rounded-circle d-inline-block" style={{ width: '6px', height: '6px' }}></span>
                  {selectedLabelCount} {selectedLabelCount === 1 ? 'Label' : 'Labels'} Selected
                </Badge>
              )}
            </div>
          </div>
        </Accordion.Header>
        <Accordion.Body className="bg-light p-3">
          {Object.entries(groupedItems).map(([batchNumber, batchItems]) => (
            <Card key={batchNumber} className="mb-2 border shadow-none bg-white">
              <Card.Header className="py-2 px-3 bg-white border-bottom-0 d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-2">
                  <FormCheck
                    type="checkbox"
                    checked={isBatchSelected(batchNumber)}
                    onChange={() => toggleBatch(item, batchNumber)}
                    id={`batch-${batchNumber}-${item._id}`}
                    className="m-0"
                  />
                  <label htmlFor={`batch-${batchNumber}-${item._id}`} className="fw-bold small text-muted cursor-pointer mb-0">
                    Batch: <span className="font-monospace text-dark">{batchNumber}</span>
                  </label>
                </div>
              </Card.Header>
              <Card.Body className="p-0">
                <BatchTable
                  item={item}
                  batchNumber={batchNumber}
                  batchItems={batchItems}
                  toggleBatch={toggleBatch}
                  updateBatchQty={updateBatchQty}
                  setViewingPurchase={setViewingPurchase}
                />
              </Card.Body>
            </Card>
          ))}
          {Object.keys(groupedItems).length === 0 && (
            <div className="text-center py-4">
              <FaBoxes className="text-muted mb-2 opacity-50" size={24} />
              <div className="text-muted small fst-italic">No detailed batch information available.</div>
            </div>
          )}
        </Accordion.Body>
      </Accordion.Item>
    </Card>
  );
};

// ----------------------------------------------------------------------
// MAIN COMPONENT
// ----------------------------------------------------------------------

const ItemAccordion = ({ items: propItems }) => {
  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.innerText = styles;
    document.head.appendChild(styleSheet);
    return () => document.head.removeChild(styleSheet);
  }, []);

  const { values, setFieldValue } = useFormikContext();
  const dispatch = useDispatch();
  const { stocks: reduxStocks, status, pagination } = useSelector(state => state.stockReducer);

  const [searchQuery, setSearchQuery] = useState("");
  const [viewingPurchase, setViewingPurchase] = useState(null);

  // --- 1. SEARCH & SYNC HANDLERS ---
  const handleSearch = useCallback((e) => {
    if (e) e.preventDefault();
    dispatch(getAllItemsWithStock({ search: searchQuery, page: 1, limit: 12 }));
  }, [dispatch, searchQuery]);

  const handleRefresh = useCallback(() => {
    setSearchQuery("");
    dispatch(getAllItemsWithStock({ search: "", page: 1, limit: 12 }));
  }, [dispatch]);

  // Initial load
  useEffect(() => {
    if (propItems) return;
    dispatch(getAllItemsWithStock({ search: "", page: 1, limit: 12 }));
  }, [dispatch, propItems]);

  const handleLoadMore = () => {
    if (status !== 'loading' && pagination.page < pagination.pages) {
      dispatch(getAllItemsWithStock({
        search: searchQuery,
        page: pagination.page + 1,
        limit: 12,
        append: true
      }));
    }
  };

  // --- 2. ROBUST DATA NORMALIZATION ---
  const normalizedItems = useMemo(() => {
    // Hybrid logic: Prioritize propItems, fallback to reduxStocks
    const listToProcess = propItems || reduxStocks || [];
    const map = new Map();

    listToProcess.forEach(rawItem => {
      const isSer = rawItem.serialized ?? rawItem.isSerialized ?? false;

      let uniqueId = rawItem.uId;
      if (!uniqueId) {
        uniqueId = rawItem._id;
        if (rawItem.variant_id && !rawItem._id.includes(rawItem.variant_id)) {
          uniqueId = `${rawItem._id}_${rawItem.variant_id}`;
        }
      }

      const displayName = rawItem.variantName 
          ? (rawItem.variantName.toLowerCase().includes(rawItem.itemName?.toLowerCase()) 
              ? rawItem.variantName 
              : `${rawItem.itemName} - ${rawItem.variantName}`)
          : rawItem.itemName;

      map.set(uniqueId, {
        ...rawItem,
        _id: uniqueId,
        serialized: isSer,
        batches: rawItem.batches || [],
        serializedItems: rawItem.serializedItems || [],
        variantAttributes: rawItem.variantAttributes || [],
        itemName: displayName,
        baseItemName: rawItem.itemName,
        variantName: rawItem.variantName,
        barcode: rawItem.barcode
      });
    });

    const list = Array.from(map.values());

    // If using propItems, apply local filtering (backward compatibility)
    // If using propItems, apply local token-based filtering
    if (propItems && searchQuery) {
      const tokens = searchQuery.toLowerCase().split(/\s+/).filter(Boolean);
      return list.filter(i => {
        const itemText = [
          i.itemName,
          i.baseItemName,
          i.variantName,
          i.barcode,
          ...(i.serializedItems || []).map(s => s.serialNumber || ""),
          ...(i.batches || []).map(b => b.batch_number || "")
        ].join(" ").toLowerCase();

        return tokens.every(token => itemText.includes(token));
      });
    }

    return list;
  }, [reduxStocks, propItems, searchQuery]);

  // --- 2. SELECTION HELPERS ---
  const isItemSelected = (itemId) => values.printItems.some(p => p._id === itemId);
  const isBatchSelected = (itemId, batchNumber) => {
    const item = values.printItems.find(p => p._id === itemId);
    return item?.batchNumbers?.includes(batchNumber) || false;
  };

  // --- 3. LOGIC HANDLERS ---

  // Smart Toggle for Items (Select All Batches)
  const toggleItem = (item) => {
    const currentList = [...values.printItems];
    const idx = currentList.findIndex(p => p._id === item._id);

    if (idx !== -1) {
      currentList.splice(idx, 1);
    } else {
      const batchNumbers = [...new Set([
        ...(item.batches?.map(b => b.batch_number) || []),
        ...(item.serializedItems?.map(s => s.batch_number) || [])
      ])];
      if (batchNumbers.length === 0 && item.totalStock > 0) batchNumbers.push("AGGREGATED");

      const batchData = {};
      // Default logic: Select Qty 1 instead of full stock (to prevent massive unwanted prints)
      if (!item.serialized || (item.serializedItems && item.serializedItems.length === 0)) {
        item.batches?.forEach(b => {
          const batchB2b = b.b2bPrice || (b.b2bPricing && b.b2bPricing[0]?.price) || item.b2bPrice || (item.b2bPricing && item.b2bPricing[0]?.price);
          batchData[b.batch_number] = {
            qty: 1,
            sellingPrice: b.sellingPrice || item.lastSellingPrice,
            b2bPrice: batchB2b,
            b2bPricing: b.b2bPricing || (batchB2b ? [{ minQty: 1, price: batchB2b }] : []),
            barcode: b.barcode || item.barcode,
            itemName: b.itemName || item.itemName
          };
        });
        if (batchNumbers.includes("AGGREGATED") && !batchData["AGGREGATED"]) {
          const aggB2b = item.b2bPrice || (item.b2bPricing && item.b2bPricing[0]?.price);
          batchData["AGGREGATED"] = {
            qty: 1, // 1 label by default
            sellingPrice: item.lastSellingPrice,
            b2bPrice: aggB2b,
            b2bPricing: item.b2bPricing || (aggB2b ? [{ minQty: 1, price: aggB2b }] : []),
            barcode: item.barcode,
            itemName: item.itemName
          };
        }
      }

      currentList.push({
        ...item,
        batchNumbers,
        batchData,
        serializedItems: item.serialized ? [...item.serializedItems] : []
      });
    }
    setFieldValue("printItems", currentList);
  };

  // Smart Toggle for Batches
  const toggleBatch = (item, batchNumber, serialNumber = null) => {
    const currentList = [...values.printItems];
    let idx = currentList.findIndex(p => p._id === item._id);

    // Add item shell if missing
    if (idx === -1) {
      currentList.push({ ...item, batchNumbers: [], serializedItems: [], batchData: {} });
      idx = currentList.length - 1;
    }
    const selectedItem = currentList[idx];

    if (item.serialized && serialNumber) {
      // Serial Toggle Logic
      const sIndex = selectedItem.serializedItems.findIndex(s => s.serialNumber === serialNumber);
      if (sIndex !== -1) selectedItem.serializedItems.splice(sIndex, 1);
      else {
        const serialObj = item.serializedItems.find(s => s.serialNumber === serialNumber);
        if (serialObj) selectedItem.serializedItems.push(serialObj);
      }

      // Sync Batch selection based on Serial presence
      const hasSerialsForBatch = selectedItem.serializedItems.some(s => s.batch_number === batchNumber);
      if (hasSerialsForBatch && !selectedItem.batchNumbers.includes(batchNumber)) selectedItem.batchNumbers.push(batchNumber);
      else if (!hasSerialsForBatch) selectedItem.batchNumbers = selectedItem.batchNumbers.filter(b => b !== batchNumber);

    } else {
      // Bulk Toggle Logic
      if (selectedItem.batchNumbers.includes(batchNumber)) {
        selectedItem.batchNumbers = selectedItem.batchNumbers.filter(b => b !== batchNumber);
        if (selectedItem.batchData) delete selectedItem.batchData[batchNumber];
      } else {
        selectedItem.batchNumbers.push(batchNumber);
        // Default to 1 label per batch when checking box (prevents runaway 1000+ prints)
        const batchInfo = item.batches?.find(b => b.batch_number === batchNumber);
        const qty = 1;
        const price = batchNumber === "AGGREGATED" ? item.lastSellingPrice : (batchInfo?.sellingPrice || item.lastSellingPrice);

        if (!selectedItem.batchData) selectedItem.batchData = {};
        selectedItem.batchData[batchNumber] = { qty, sellingPrice: price, barcode: item.barcode, itemName: item.itemName };
      }
    }

    // Cleanup empty items
    if (selectedItem.batchNumbers.length === 0 && selectedItem.serializedItems.length === 0) {
      currentList.splice(idx, 1);
    }
    setFieldValue("printItems", currentList);
  };

  // Smart Qty Update (Auto-Selects Batch)
  const updateBatchQty = (item, batchNumber, val, batchItem) => {
    const currentList = [...values.printItems];
    let idx = currentList.findIndex(p => p._id === item._id);

    // Auto-add item if not present
    if (idx === -1) {
      currentList.push({ ...item, batchNumbers: [], serializedItems: [], batchData: {} });
      idx = currentList.length - 1;
    }

    const selectedItem = currentList[idx];

    if (val > 0) {
      // Ensure batch is selected
      if (!selectedItem.batchNumbers.includes(batchNumber)) {
        selectedItem.batchNumbers.push(batchNumber);
      }
      // Update Data
      if (!selectedItem.batchData) selectedItem.batchData = {};
      selectedItem.batchData[batchNumber] = {
        qty: val,
        sellingPrice: batchItem.sellingPrice || item.lastSellingPrice,
        b2bPrice: batchItem.b2bPrice || (batchItem.b2bPricing && batchItem.b2bPricing[0]?.price) || item.b2bPrice,
        barcode: batchItem.barcode || item.barcode,
        itemName: batchItem.itemName || item.itemName
      };
    } else {
      // If 0 or empty, deselect batch
      selectedItem.batchNumbers = selectedItem.batchNumbers.filter(b => b !== batchNumber);
      if (selectedItem.batchData) delete selectedItem.batchData[batchNumber];
    }

    // Cleanup
    if (selectedItem.batchNumbers.length === 0 && selectedItem.serializedItems.length === 0) {
      currentList.splice(idx, 1);
    }

    setFieldValue("printItems", currentList);
  };

  return (
    <div>
      {/* GROUNDED HEADER SECTION */}
      <div className="bg-white border rounded-3 p-3 mb-4 shadow-sm">
        <div className="d-flex justify-content-between align-items-center gap-3">
          <form onSubmit={handleSearch} className="flex-grow-1" style={{ maxWidth: '650px' }}>
            <label className="small text-muted fw-bold mb-2 d-block">Search & Inventory Lookup</label>
            <InputGroup className="border rounded-3 overflow-hidden bg-white search-input-group transition-all">
              <InputGroup.Text className="bg-white border-0 ps-3">
                <FaSearch className={status === 'loading' ? "text-primary animate-pulse" : "text-muted"} />
              </InputGroup.Text>
              <FormControl
                placeholder={propItems ? "Filter current list..." : "Search by Item, Barcode, Serial, Purchase Ref, or Bill #..."}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="border-0 py-2 ps-2 shadow-none bg-transparent"
                style={{ fontSize: '1rem', fontWeight: '500' }}
              />
              <Button
                type="submit"
                variant="primary"
                className="px-4 fw-bold border-0 shadow-none d-flex align-items-center gap-2"
                style={{ background: '#3b82f6' }}
                disabled={status === 'loading'}
              >
                {status === 'loading' ? <Spinner animation="border" size="sm" /> : <FaSearch size={14} />}
                {status === 'loading' ? "Searching..." : "Search"}
              </Button>
              <Button
                variant="light"
                className="px-3 border-0 border-start bg-white text-secondary d-flex align-items-center hover-bg-light"
                onClick={handleRefresh}
                title="Refresh / Sync All"
              >
                <FaSync size={14} className={status === 'loading' ? "fa-spin" : "opacity-75"} />
              </Button>
            </InputGroup>
          </form>

          <div className="d-flex flex-column align-items-end gap-1">
            <label className="small text-muted fw-bold mb-1">Batch Actions</label>
            <div className="d-flex gap-2">
              {!propItems && (
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="px-3 fw-bold rounded-3 py-2 shadow-none bg-white border-2"
                  onClick={handleRefresh}
                  disabled={status === 'loading'}
                >
                  <FaSync className={`me-2 ${status === 'loading' ? 'fa-spin' : ''}`} />
                  Sync All
                </Button>
              )}
              <Button
                variant="dark"
                size="sm"
                className="px-4 fw-bold rounded-3 py-2 shadow-none border-0"
                style={{ background: '#1e293b' }}
                onClick={() => {
                  const all = normalizedItems.map(i => {
                    let bNumbers = [...new Set([...(i.batches || []).map(b => b.batch_number)])];
                    if (bNumbers.length === 0 && i.totalStock > 0) bNumbers = ["AGGREGATED"];
                    const bData = {};
                    if (!i.serialized || i.serializedItems?.length === 0) {
                      (i.batches || []).forEach(b => {
                        bData[b.batch_number] = {
                          qty: 1, // Changed from b.availableQty to 1
                          sellingPrice: b.sellingPrice || i.lastSellingPrice,
                          barcode: i.barcode,
                          itemName: i.itemName
                        };
                      });
                      if (bNumbers.includes("AGGREGATED")) bData["AGGREGATED"] = { qty: 1, sellingPrice: i.lastSellingPrice, barcode: i.barcode, itemName: i.itemName };
                    }
                    return { ...i, batchNumbers: bNumbers, serializedItems: i.serializedItems || [], batchData: bData };
                  });
                  setFieldValue("printItems", all);
                }}
              >
                {propItems ? "Select All" : "Select Result Page"}
              </Button>
              <Button
                variant="outline-danger"
                size="sm"
                className="px-3 fw-bold rounded-3 py-2 shadow-none"
                onClick={() => setFieldValue("printItems", [])}
              >
                Clear Selection
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* LIVE SELECTION ALERT BANNER */}
      {values.printItems.length > 0 && (() => {
        let totalSelectedLabels = 0;
        values.printItems.forEach(itm => {
          if (itm.serialized) {
            totalSelectedLabels += (itm.serializedItems?.length || 0);
          } else {
            if (itm.batchData && Object.keys(itm.batchData).length > 0) {
              Object.values(itm.batchData).forEach(b => {
                totalSelectedLabels += Number(b.qty) || 0;
              });
            } else {
              totalSelectedLabels += (itm.batchNumbers?.length || 1);
            }
          }
        });

        return (
          <div className="d-flex flex-wrap align-items-center justify-content-between p-2.5 px-3.5 mb-4 rounded-3 border border-primary border-opacity-50 shadow-sm animate__animated animate__fadeIn" style={{ background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)' }}>
            <div className="d-flex align-items-center gap-2.5">
              <div className="d-flex align-items-center justify-content-center bg-primary text-white rounded-circle shadow-xs" style={{ width: '30px', height: '30px' }}>
                <FaBoxes size={15} />
              </div>
              <div>
                <span className="fw-bold text-dark small me-2">
                  {values.printItems.length} {values.printItems.length === 1 ? 'Product' : 'Products'} Selected
                </span>
                <span className="badge bg-primary text-white rounded-pill px-2.5 py-1 font-monospace">
                  {totalSelectedLabels} {totalSelectedLabels === 1 ? 'Label' : 'Labels'} Queued
                </span>
              </div>
            </div>
            <Button
              variant="outline-danger"
              size="sm"
              className="py-1 px-3 x-small rounded-pill fw-bold bg-white shadow-xs"
              onClick={() => setFieldValue("printItems", [])}
            >
              Clear All ({values.printItems.length})
            </Button>
          </div>
        );
      })()}

      <div className="position-relative">
        {status === 'loading' && normalizedItems.length > 0 && (
          <div
            className="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-start pt-5 rounded-3 z-index-10"
            style={{
              background: 'rgba(255, 255, 255, 0.6) ',
              backdropFilter: 'blur(2px)',
              zIndex: 10,
              paddingTop: '3rem'
            }}
          >
            <Card className="shadow-lg border-0 p-4 d-flex align-items-center gap-3">
              <Spinner animation="grow" variant="primary" />
              <span className="fw-bold text-dark">Updating Inventory Flow...</span>
            </Card>
          </div>
        )}

        <Accordion alwaysOpen className={status === 'loading' ? 'opacity-25' : ''}>
          {normalizedItems.length === 0 ? (
            <div className="text-center p-5 border rounded-3 bg-light">
              <Spinner
                animation={status === 'loading' ? "border" : undefined}
                variant="primary"
                className={status === 'loading' ? "mb-3" : "d-none"}
              />
              <div className={status === 'loading' ? 'h5 text-primary' : 'h5 text-muted'}>
                {status === 'loading' ? "Pulling stock records..." : "No items found."}
              </div>
              <p className="text-secondary small mb-0">
                {status === 'loading' ? "Please wait while we sync with the inventory server..." : "Try adjusting your search filters or sync all items."}
              </p>
            </div>
          ) : (
            normalizedItems.map((item) => (
              <ItemRow
                key={item._id}
                item={item}
                isItemSelected={isItemSelected(item._id)}
                isBatchSelected={(batch) => isBatchSelected(item._id, batch)}
                toggleItem={toggleItem}
                toggleBatch={toggleBatch}
                updateBatchQty={updateBatchQty}
                setViewingPurchase={setViewingPurchase}
              />
            ))
          )}
        </Accordion>
      </div>

      {pagination.page < pagination.pages && (
        <div className="d-flex justify-content-center mt-4 mb-2">
          <Button
            variant="outline-secondary"
            className="px-5 rounded-pill shadow-sm"
            onClick={handleLoadMore}
            disabled={status === 'loading'}
          >
            {status === 'loading' ? (
              <Spinner animation="border" size="sm" className="me-2" />
            ) : (
              <FaChevronDown className="me-2" />
            )}
            Load More Items ({normalizedItems.length} of {pagination.total})
          </Button>
        </div>
      )}

      {viewingPurchase && (
        <StockDetails
          show={!!viewingPurchase}
          purchaseId={viewingPurchase}
          onClose={() => setViewingPurchase(null)}
        />
      )}
    </div>
  );
};

export default ItemAccordion;